import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { getCachedSignature, cacheSignature, hasValidCachedSignatures } from './signatureCache';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
  validateStatus: status => status < 500
});

// Debug flag - set to false to disable request/response logging
const DEBUG_API = true;

// Session expiry time (14 days in milliseconds)
const SESSION_EXPIRY = 14 * 24 * 60 * 60 * 1000;

// Function to check if token is still valid
const isTokenValid = (token) => {
  try {
    // First check if it's a JWT token (contains two dots)
    if (token.includes('.')) {
      // It's a JWT token, we'll assume it's valid for now
      // In a production environment, you'd want to decode and verify the expiry
      return true;
    }

    // Legacy format: Token is stored as base64 encoded JSON
    try {
      const decoded = JSON.parse(atob(token));

      // Check if token has explicit expiry
      if (decoded.expiry) {
        return Date.now() < decoded.expiry;
      }

      // Fallback to checking timestamp
      if (decoded.timestamp) {
        return (Date.now() - decoded.timestamp) < SESSION_EXPIRY;
      }
    } catch (decodeError) {
      console.warn('Token is not in expected format, treating as valid');
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking token validity:', error);
    return false;
  }
};

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from local storage - tokens should be stored without Bearer prefix
    const token = localStorage.getItem('token');
    const walletAddress = localStorage.getItem('wallet_address');
    const isAdminSession = localStorage.getItem('isAdminSession') === 'true';

    // Check if token is valid
    const tokenValid = token && isTokenValid(token);
    const hasCachedSigs = walletAddress && hasValidCachedSignatures(walletAddress);

    if (tokenValid || hasCachedSigs) {
      // Always add 'Bearer ' prefix for all API requests
      // This is required by the server's JWT verification
      const cleanToken = token?.startsWith('Bearer ')
        ? token.substring(7).trim() // Remove any existing Bearer prefix
        : (token || '').trim();

      if (cleanToken) {
        config.headers.Authorization = `Bearer ${cleanToken}`;
      }

      // Check if we have a cached signature for this request
      // Only attempt to use cached signatures for specific endpoints that require wallet signatures
      if (walletAddress && config.url &&
          (config.url.includes('/wallet/') ||
           config.url.includes('/quests/') ||
           config.url.includes('/auth/'))) {

        const requestBody = config.data ? JSON.stringify(config.data) : '';
        const cacheKey = `${config.method}-${config.url}-${requestBody}`;
        const cachedSignature = getCachedSignature(cacheKey, walletAddress);

        if (cachedSignature) {
          // If we have a cached signature, add it to the headers
          config.headers['X-Wallet-Signature'] = cachedSignature;
          // Add a flag to indicate we're using a cached signature
          config._usingCachedSignature = true;
        }
      }

      // Enhanced handling for admin routes
      const isAdminRoute = isAdminSession ||
                         window.location.pathname.includes('admin') ||
                         (config.url && (config.url.includes('/admin/') ||
                                        config.url.includes('/quests/admin/') ||
                                        config.url.includes('/tournaments/admin/') ||
                                        config.url.includes('/tasks/admin/')));

      // For admin requests, ensure we're using the JWT token properly
      // This is critical for admin API endpoints to work correctly
      if (isAdminRoute && token) {
        // Make sure we're using the JWT token format expected by the server
        // Always ensure the token is clean (no Bearer prefix) before adding it
        const finalToken = cleanToken.trim();

        // Set the Authorization header with Bearer prefix
        config.headers.Authorization = `Bearer ${finalToken}`;

        // Add a custom header to indicate this is an admin request
        config.headers['X-Admin-Request'] = 'true';

        // Add debug header to help with troubleshooting
        config.headers['X-Debug-Admin'] = 'true';

        console.log('API Client - Admin Request:', {
          url: config.url,
          path: window.location.pathname,
          token: finalToken ? (finalToken.substring(0, 10) + '...') : 'none',
          headerValue: config.headers.Authorization ? (config.headers.Authorization.substring(0, 20) + '...') : 'none',
          isAdminSession,
          isAdminRoute
        });
      }
    }

    // Only log requests when debug is enabled and for non-GET methods
    if (DEBUG_API && config.method !== 'get') {
      console.log('API Client - Request Config:', {
        url: config.url,
        method: config.method,
        headers: config.headers,
        data: config.data,
        origin: window.location.origin,
        pathname: window.location.pathname,
        search: window.location.search
      });
    }

    return config;
  },
  (error) => {
    console.error('API Client - Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    // Only log responses when debug is enabled and for non-GET requests
    if (DEBUG_API && response.config.method !== 'get') {
      console.log('API Client - Response:', {
        status: response.status,
        data: response.data
      });
    }

    // If this was a successful response and has a wallet signature in the headers
    // Cache the signature for future use if it wasn't already from the cache
    if (response.status >= 200 && response.status < 300) {
      const walletAddress = localStorage.getItem('wallet_address');
      const signature = response.headers?.['x-wallet-signature'];

      if (signature && walletAddress && !response.config._usingCachedSignature) {
        // Create a cache key based on the request
        const requestBody = response.config.data ? JSON.stringify(response.config.data) : '';
        const cacheKey = `${response.config.method}-${response.config.url}-${requestBody}`;

        // Cache the signature
        cacheSignature(cacheKey, signature, walletAddress);
        console.log('Cached signature from API response for future use');
      }

      // Check if we need to extend the token expiry
      const token = localStorage.getItem('token');
      if (token) {
        // Only try to extend legacy tokens (not JWT tokens)
        if (!token.includes('.')) {
          try {
            const decoded = JSON.parse(atob(token));
            const now = Date.now();

            // If token is more than 70% through its lifetime, extend it
            if (decoded.timestamp && (now - decoded.timestamp > SESSION_EXPIRY * 0.7)) {
              console.log('Token nearing expiry, extending timestamp');
              decoded.timestamp = now;
              decoded.expiry = now + SESSION_EXPIRY;

              // Save the updated token
              const updatedToken = btoa(JSON.stringify(decoded));
              localStorage.setItem('token', updatedToken);

              // Update user object if it exists
              const savedUser = localStorage.getItem('user');
              if (savedUser) {
                const parsedUser = JSON.parse(savedUser);
                parsedUser.timestamp = now;
                parsedUser.sessionExpiry = now + SESSION_EXPIRY;
                parsedUser.token = updatedToken;
                localStorage.setItem('user', JSON.stringify(parsedUser));
              }
            }
          } catch (error) {
            console.warn('Token is not in expected format, skipping extension');
          }
        }
      }
    }

    return response.data;
  },
  async (error) => {
    // Enhanced error logging with more details
    console.error('API Client - Response Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      origin: window.location.origin,
      pathname: window.location.pathname,
      search: window.location.search
    });

    if (error.response) {
      // Get error message and code from response with more fallbacks
      const errorData = error.response.data || {};
      const errorMessage = errorData.error ||
                         errorData.message ||
                         error.response.statusText ||
                         `Server error: ${error.response.status}` ||
                         'An error occurred';

      // Create a custom error object with additional properties
      const customError = new Error(errorMessage);
      customError.response = error.response;
      customError.status = error.response.status;
      customError.code = errorData.code;
      customError.data = errorData;

      // For 404 errors, provide a more specific message
      if (error.response.status === 404) {
        // Check if it's a room not found error
        if (error.config?.url?.includes('/duel/rooms/')) {
          // This is expected behavior - the room doesn't exist in the database
          // but it might exist in localStorage
          console.log('Room not found in database, will try localStorage');
          return { success: false, error: 'Room not found in database' };
        }
      }

      throw customError;
    }

    // Handle network errors with more details
    if (error.request) {
      console.error('Network Error:', {
        request: error.request,
        url: error.config?.url,
        method: error.config?.method
      });
      throw new Error(`Network error: ${error.message}. Please check your connection.`);
    }

    // Handle other errors
    throw error;
  }
);

// Helper methods for common operations
export const api = {
  get: (endpoint, config = {}) => apiClient.get(endpoint, config),
  post: (endpoint, data = {}, config = {}) => apiClient.post(endpoint, data, config),
  put: (endpoint, data = {}, config = {}) => apiClient.put(endpoint, data, config),
  delete: (endpoint, config = {}) => apiClient.delete(endpoint, config),
  // File upload helper
  upload: (endpoint, formData, config = {}) => {
    return apiClient.post(endpoint, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export { API_ENDPOINTS as endpoints };
export default api;
